import { CommissionPayoutType } from './item';

// item 30 — commission ledger entry (mirrors CommissionIndexItemDto).
export enum CommissionStatus {
  Accrued = 0,
  Submitted = 1,
  Approved = 2,
  Paid = 3,
  // item 741b — subscription cancelled before the month was paid: kept as a record,
  // terminal (unselectable, excluded from totals/payroll)
  Cancelled = 4,
  // item 1167 — admin reversed (mis-accrual): stays visible grayed, out of totals,
  // unsubmittable; the only exit is the explicit Reopen action
  Reversed = 5,
}

// item 165 — the ledger-entry kind. Accrued rows are Commission; an admin can hand-add
// Reimbursement / Credit / Adjustment (Adjustment may be negative).
export enum CommissionEntryType {
  Commission = 0,
  Reimbursement = 1,
  Credit = 2,
  Adjustment = 3,
  General = 4, // item 165b — catch-all manual entry (may be negative)
}

export interface Commission {
  id: string;
  earnedByUserId: string;
  earnedByName: string;
  customerId?: string;
  customerName?: string;
  proposalId?: string;
  proposalNumber?: string;
  proposalTitle?: string; // item 504 — the job / service name
  workOrderNumber?: string; // item 535 — job context (proposal-less deals)
  workOrderTitle?: string;
  invoiceNumber?: string;
  sourceType: CommissionPayoutType;
  // item 495 — v3 (item 316) tenant-defined reason name (Sales PO / Project Lead / custom);
  // null for legacy rows that only carry sourceType
  commissionReasonId?: string;
  reasonName?: string;
  entryType: CommissionEntryType;
  basisAmount: number;
  rate: number;
  amount: number;
  description: string;
  status: CommissionStatus;
  createdDate: string;
  paidDate?: string;
  // dart (2026-07-14) — #26: recurring context (null/false on one-time rows). recurringStatus is
  // the subscription's Active/Paused/Cancelled state (RecurringRevenueStatus); accrualPeriod is the
  // month a recurring row booked for.
  recurringRevenueId?: string;
  isRecurring?: boolean;
  recurringStatus?: number;
  recurringName?: string; // item 741c — the subscription's name = the JOB a recurring row pays on
  attachmentFileId?: string | null; // item 1198 — receipt/backup file on a hand-added entry
  accrualPeriod?: string;
}

export interface CommissionsDto {
  index: Commission[];
  length: number;
}

export const commissionSourceLabel = (t: CommissionPayoutType): string => {
  switch (t) {
    case CommissionPayoutType.ProjectLead: return 'Project lead';
    case CommissionPayoutType.IOSales: return 'IO Sales';
    case CommissionPayoutType.SalesPO: return 'Sales PO';
    case CommissionPayoutType.DevelopingPartner: return 'Developing partner';
    default: return '—';
  }
};

export const COMMISSION_ENTRY_TYPE_LABELS: Record<CommissionEntryType, string> = {
  [CommissionEntryType.Commission]: 'Commission',
  [CommissionEntryType.Reimbursement]: 'Reimbursement',
  [CommissionEntryType.Credit]: 'Credit',
  [CommissionEntryType.Adjustment]: 'Adjustment',
  [CommissionEntryType.General]: 'General',
};

// item 165b — the three things an admin can hand-add to the ledger. The two real
// commission types (Commission, Project Lead) plus a General catch-all; Project Lead
// is the third item. Each maps to the (entryType, sourceType) the backend stores.
export interface CommissionAddOption {
  key: string;
  label: string;
  entryType: CommissionEntryType;
  sourceType: CommissionPayoutType;
  negativeAllowed: boolean;
  // the amount is always stored negative (a deduction) — the user types a positive figure
  forceNegative?: boolean;
}
export const COMMISSION_ADD_OPTIONS: CommissionAddOption[] = [
  {
    // item 186 — a generic manual sales commission; no single auto-reason applies, so it
    // carries None as its source tag (the entry type already labels it "Commission").
    key: 'commission',
    label: 'Commission',
    entryType: CommissionEntryType.Commission,
    sourceType: CommissionPayoutType.None,
    negativeAllowed: false,
  },
  {
    key: 'general',
    label: 'General',
    entryType: CommissionEntryType.General,
    sourceType: CommissionPayoutType.None,
    negativeAllowed: true,
  },
  {
    key: 'projectLead',
    label: 'Project Lead',
    entryType: CommissionEntryType.Commission,
    sourceType: CommissionPayoutType.ProjectLead,
    negativeAllowed: false,
  },
];

// item 486 — what an EARNER can add to their OWN ledger from My Book. Same doc pipeline as a
// commission (starts Accrued → the earner submits the batch → an admin approves it on the
// accountant dashboard, which mints the COMM- doc), so nobody can self-pay. A reimbursement or
// one-off payout is positive; a deduction is stored negative (typed as a positive figure).
export const COMMISSION_SELF_ADD_OPTIONS: CommissionAddOption[] = [
  {
    key: 'reimbursement',
    label: 'Reimbursement',
    entryType: CommissionEntryType.Reimbursement,
    sourceType: CommissionPayoutType.None,
    negativeAllowed: false,
  },
  {
    key: 'payout',
    label: 'One-off payout',
    entryType: CommissionEntryType.General,
    sourceType: CommissionPayoutType.None,
    negativeAllowed: false,
  },
  {
    key: 'deduction',
    label: 'Deduction',
    entryType: CommissionEntryType.General,
    sourceType: CommissionPayoutType.None,
    negativeAllowed: true,
    forceNegative: true,
  },
];

// item 165/495 — the Type column: accrued rows show their v3 tenant-defined reason name (Sales
// PO / Project Lead / a custom reason — item 316 replaced the fixed payout-type engine, so
// sourceType is empty on v3 rows); fall back to the legacy payout label for pre-v3 rows that
// only carry sourceType. Manual entries show their entry-kind (Reimbursement / Credit / General).
// item 740 (Chad) — subscription accruals are MARKED as recurring: a monthly-accrual row with no
// reason of its own says "Recurring" (the description already carries the sub name + period).
// item 765 (Chad) — a recurring row that knows its reason reads "Recurring · IO Sales".
export const commissionTypeLabel = (c: Commission): string => {
  if (c.entryType !== CommissionEntryType.Commission) return COMMISSION_ENTRY_TYPE_LABELS[c.entryType];
  if (c.isRecurring) return c.reasonName ? `Recurring · ${c.reasonName}` : 'Recurring';
  return c.reasonName || commissionSourceLabel(c.sourceType);
};
